import React from "react";
import {
  StructuredListWrapper,
  StructuredListHead,
  StructuredListBody,
  StructuredListRow,
  StructuredListCell,
  TooltipDefinition,
} from "carbon-components-react";
import classnames from "classnames";

const styles = require("./styles.module.scss");

interface LabeledListData {
  label: string;
  value: string | React.ReactElement;
  tooltip?: string;
  labelAlign?: "left" | "right" | "center";
  valueAlign?: "left" | "right" | "center";
}

type Props = {
  listData: LabeledListData[];
  title?: string;
  subtitle?: string;
  kind?: "default" | "minimal" | "minimal-half-width";
  size?: "compact" | "short" | "normal" | "tall";
};

// Given a 'size' attribute, returns appropriate styling object for rows and heights
const sizeToStyles = (size: string) => {
  let rowStyle: any = {};
  let cellStyle: any = {};

  // padding left should always be 5px
  const defaultRowStyle = {};
  const defaultCellStyle = { padding: "5px", paddingLeft: "5px" };

  switch (size) {
    case "compact":
      rowStyle = { height: "2rem" };
      cellStyle = { padding: "3px" };
      break;
    case "short":
      rowStyle = { height: "3rem" };
      break;
    case "normal":
      rowStyle = { height: "3.5rem" };
      break;
    case "tall":
      rowStyle = { height: "4rem" };
      cellStyle = { padding: "8px" };
      break;
    default:
      break;
  }

  return {
    rowStyle: { ...defaultRowStyle, ...rowStyle },
    cellStyle: { ...defaultCellStyle, ...cellStyle },
  };
};

const LabeledObject: React.FC<Props> = ({
  listData,
  title,
  subtitle = "",
  size = "normal",
  kind = "default",
}) => {
  const createTooltip = (label, tooltip) => {
    return <TooltipDefinition tooltipText={tooltip}>{label}</TooltipDefinition>;
  };

  const { rowStyle, cellStyle } = sizeToStyles(size);

  const isMinimal = kind === "minimal";
  const isMinimalHalfWidth = kind === "minimal-half-width";

  return (
    <div
      className={classnames(
        styles.labeledList,
        isMinimal && styles.kindMinimal,
        isMinimalHalfWidth && styles.kindMinimalHalfWidth,
      )}
    >
      {subtitle && <h4 className={styles.labeledObjectSubtitle}>{subtitle}</h4>}
      {title && <h3 className={styles.labeledObjectTitle}>{title}</h3>}
      {listData.length > 0 && (
        <StructuredListWrapper>
          {!isMinimal && (
            <StructuredListHead>
              <StructuredListRow head>
                <StructuredListCell head />
                <StructuredListCell head />
              </StructuredListRow>
            </StructuredListHead>
          )}
          <StructuredListBody>
            {listData.map(({ label, value, tooltip, valueAlign = "left", labelAlign = "left" }) => (
              <StructuredListRow style={rowStyle} key={label}>
                <StructuredListCell
                  style={{ fontWeight: "bold", ...cellStyle }}
                  className={
                    styles[`textAlign${labelAlign.charAt(0).toUpperCase() + labelAlign.slice(1)}`]
                  }
                >
                  {tooltip ? createTooltip(label, tooltip) : label}
                </StructuredListCell>
                <StructuredListCell
                  style={cellStyle}
                  className={
                    styles[`textAlign${valueAlign.charAt(0).toUpperCase() + valueAlign.slice(1)}`]
                  }
                >
                  {value}
                </StructuredListCell>
              </StructuredListRow>
            ))}
          </StructuredListBody>
        </StructuredListWrapper>
      )}
    </div>
  );
};

export default LabeledObject;
